import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Sidebar from '../Sidebar/Sidebar';
import './home.css';

const startOf = (d) => { const r = new Date(d); r.setHours(0, 0, 0, 0); return r; };
const endOf   = (d) => { const r = new Date(d); r.setHours(23, 59, 59, 0); return r; };

const PRESETS = [
  { label: 'Today',     get: () => ({ start: startOf(new Date()), end: new Date() }) },
  { label: 'Yesterday', get: () => { const y = new Date(); y.setDate(y.getDate() - 1); return { start: startOf(y), end: endOf(y) }; } },
  { label: '1 Week',    get: () => { const s = new Date(); s.setDate(s.getDate() - 7);   return { start: startOf(s), end: new Date() }; } },
  { label: '1 Month',   get: () => { const s = new Date(); s.setMonth(s.getMonth() - 1); return { start: startOf(s), end: new Date() }; } },
  { label: '3 Months',  get: () => { const s = new Date(); s.setMonth(s.getMonth() - 3); return { start: startOf(s), end: new Date() }; } },
  { label: '6 Months',  get: () => { const s = new Date(); s.setMonth(s.getMonth() - 6); return { start: startOf(s), end: new Date() }; } },
];

const fmtDateTime = (d) => {
  if (!d) return null;
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

const buildParams = (start, end, adId) => {
  const params = new URLSearchParams();
  if (start && end) {
    params.set('start_date', fmtDateTime(start));
    params.set('end_date',   fmtDateTime(end));
  }
  if (adId) params.set('ad_id', adId);
  return params.toString();
};

const Home = () => {
  const baseURL  = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();

  const [startDate,    setStartDate]    = useState(null);
  const [endDate,      setEndDate]      = useState(null);
  const [activePreset, setActivePreset] = useState('Today');
  const [ads,          setAds]          = useState([]);
  const [selectedAd,   setSelectedAd]   = useState('');

  const [kpi, setKpi] = useState({
    total_leads_today: 0,
    total_active_ads: 0,
    total_whatsapp_sent_today: 0,
    total_whatsapp_sent: 0,
    total_fetch_leads: 0,
    loading: true,
  });

  const [breakdown,     setBreakdown]     = useState({ zero: [], nonZero: [], loading: true });
  const [zeroSearch,    setZeroSearch]    = useState('');
  const [nonZeroSearch, setNonZeroSearch] = useState('');

  const getToken = () =>
    localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

  /* ── Fetch KPI stats ── */
  const fetchStats = async (start, end, adId = selectedAd) => {
    const token = getToken();
    if (!token) { navigate('/'); return; }
    setKpi(prev => ({ ...prev, loading: true }));
    try {
      const qs  = buildParams(start, end, adId);
      const res = await fetch(`${baseURL}/get_dashboard_stats/${qs ? `?${qs}` : ''}`, {
        headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' },
      });
      if (res.ok) {
        const json = await res.json();
        setKpi({ ...json.data, loading: false });
      } else {
        setKpi(prev => ({ ...prev, loading: false }));
      }
    } catch {
      setKpi(prev => ({ ...prev, loading: false }));
    }
  };

  /* ── Fetch ads breakdown — backend always returns yesterday's data ── */
  const fetchBreakdown = async (adId = selectedAd) => {
    const token = getToken();
    if (!token) return;
    setBreakdown(prev => ({ ...prev, loading: true }));

    try {
      const qs  = adId ? `ad_id=${adId}` : '';
      const res = await fetch(`${baseURL}/get_leads_breakdown/${qs ? `?${qs}` : ''}`, {
        headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' },
      });
      if (res.ok) {
        const json = await res.json();
        const list = json.data || [];
        setBreakdown({
          zero:    list.filter(a => (a.total_lead_count ?? 0) === 0),
          nonZero: list.filter(a => (a.total_lead_count ?? 0) > 0)
                       .sort((a, b) => b.total_lead_count - a.total_lead_count),
          loading: false,
        });
      } else {
        setBreakdown(prev => ({ ...prev, loading: false }));
      }
    } catch {
      setBreakdown(prev => ({ ...prev, loading: false }));
    }
  };

  /* ── Fetch ads list for dropdown ── */
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    fetch(`${baseURL}/get_ads/`, {
      headers: { Authorization: `Token ${token}`, 'Cache-Control': 'no-cache' },
    })
      .then(r => r.ok ? r.json() : null)
      .then(json => { if (json) setAds(json.data || []); })
      .catch(() => {});
  }, [baseURL]);

  /* ── Initial load — Today ── */
  useEffect(() => {
    const { start, end } = PRESETS[0].get();
    setStartDate(start);
    setEndDate(end);
    fetchStats(start, end, '');
    fetchBreakdown('');          // always yesterday
  }, []); // eslint-disable-line

  const handleAdChange = (e) => {
    const adId = e.target.value;
    setSelectedAd(adId);
    fetchStats(startDate, endDate, adId);
  };

  const applyPreset = (preset) => {
    const { start, end } = preset.get();
    setStartDate(start);
    setEndDate(end);
    setActivePreset(preset.label);
    fetchStats(start, end);      // breakdown stays on yesterday — no re-fetch
  };

  const handleApply = () => {
    if (startDate && endDate) {
      setActivePreset('');
      fetchStats(startDate, endDate); // breakdown stays on yesterday — no re-fetch
    }
  };

  const handleReset = () => {
    const { start, end } = PRESETS[0].get();
    setStartDate(start);
    setEndDate(end);
    setActivePreset('Today');
    setSelectedAd('');
    fetchStats(start, end, '');
  };

  /* ── KPI card definitions ── */
  const cards = [
    { icon: '📥', value: kpi.total_leads_today,                          label: 'Leads Today',       className: 'kpi-card kpi-blue',            href: null         },
    { icon: '📢', value: kpi.total_active_ads,                           label: 'Active Ads',        className: 'kpi-card kpi-purple',          href: null         },
    { icon: '📨', value: kpi.total_whatsapp_sent_today.toLocaleString('en-IN'), label: 'Total Sent Leads',  className: 'kpi-card kpi-teal kpi-link',   href: '/send_leads'  },
    { icon: '📊', value: kpi.total_whatsapp_sent.toLocaleString('en-IN'),       label: 'Total Leads',       className: 'kpi-card kpi-pink',            href: null         },
    { icon: '🗂️', value: kpi.total_fetch_leads.toLocaleString('en-IN'),        label: 'Total Fetch Leads', className: 'kpi-card kpi-indigo kpi-link', href: '/fetch_leads' },
  ];

  /* ── Ad row renderer ── */
  const AdRow = ({ ad, showCount }) => (
    <div className="ad-row">
      <span className="ad-row-name">{ad.builder_name || '—'}</span>
      <span className="ad-row-account">{ad.account_user || <em className="ad-row-none">No account</em>}</span>
      {showCount && <span className="ad-row-count">{(ad.total_lead_count ?? 0).toLocaleString('en-IN')}</span>}
    </div>
  );

  const BreakdownSkeleton = () => (
    <div className="ad-row-skeleton-wrap">
      {[1, 2, 3].map(i => <div key={i} className="ad-row-skeleton" />)}
    </div>
  );

  return (
    <div className="home-page container-fluid">
      <div className="row flex-nowrap">
        <div className="col-auto p-0"><Sidebar /></div>

        <main className="home-main col">

          {/* Title */}
          <div className="home-header-bar">
            <div>
              <h2 className="home-title">Overview</h2>
              <div className="home-subtitle">Today's performance at a glance</div>
            </div>
          </div>

          {/* Filter row */}
          <div className="filter-row">
            <select className="filter-ads-select" value={selectedAd} onChange={handleAdChange}>
              <option value="">All Ads</option>
              {ads.map(ad => <option key={ad.id} value={ad.id}>{ad.builder_name}</option>)}
            </select>

            <div className="filter-divider" />

            <div className="filter-presets">
              {PRESETS.map(p => (
                <button key={p.label} className={`preset-pill ${activePreset === p.label ? 'active' : ''}`} onClick={() => applyPreset(p)}>
                  {p.label}
                </button>
              ))}
            </div>

            <div className="filter-divider" />

            <DatePicker selected={startDate} onChange={d => { setStartDate(d); setActivePreset(''); }}
              selectsStart startDate={startDate} endDate={endDate} maxDate={endDate || new Date()}
              showTimeSelect timeFormat="HH:mm" timeIntervals={30} dateFormat="dd/MM/yyyy HH:mm"
              placeholderText="Start date & time" className="home-date-input" calendarClassName="tms-home-calendar"
              portalId="datepicker-portal" popperPlacement="bottom-start" />

            <span className="filter-arrow">→</span>

            <DatePicker selected={endDate} onChange={d => { setEndDate(d); setActivePreset(''); }}
              selectsEnd startDate={startDate} endDate={endDate} minDate={startDate} maxDate={new Date()}
              showTimeSelect timeFormat="HH:mm" timeIntervals={30} dateFormat="dd/MM/yyyy HH:mm"
              placeholderText="End date & time" className="home-date-input" calendarClassName="tms-home-calendar"
              portalId="datepicker-portal" popperPlacement="bottom-start" />

            <button className="filter-apply-btn" onClick={handleApply} disabled={!startDate || !endDate}>Apply</button>
            <button className="filter-reset-btn" onClick={handleReset} title="Reset to today">✕</button>
          </div>

          {/* KPI Cards */}
          <div className="kpi-row kpi-row-5">
            {cards.map(card =>
              card.href ? (
                <a key={card.label} href={card.href} className={card.className}>
                  <div className="kpi-icon">{card.icon}</div>
                  <div className="kpi-value">{kpi.loading ? <span className="kpi-skeleton" /> : card.value}</div>
                  <div className="kpi-label">{card.label}</div>
                </a>
              ) : (
                <div key={card.label} className={card.className}>
                  <div className="kpi-icon">{card.icon}</div>
                  <div className="kpi-value">{kpi.loading ? <span className="kpi-skeleton" /> : card.value}</div>
                  <div className="kpi-label">{card.label}</div>
                </div>
              )
            )}
          </div>

          {/* Ads breakdown */}
          <div className="breakdown-row">

            {/* Zero Leads */}
            <div className="breakdown-panel breakdown-zero">
              <div className="breakdown-header">
                <div className="breakdown-header-left">
                  <span className="breakdown-title">Zero Leads <span className="breakdown-date-tag">Yesterday</span></span>
                  <span className="breakdown-badge badge-zero">
                    {breakdown.loading ? '—' : breakdown.zero.filter(a =>
                      !zeroSearch ||
                      a.builder_name?.toLowerCase().includes(zeroSearch.toLowerCase()) ||
                      a.account_user?.toLowerCase().includes(zeroSearch.toLowerCase())
                    ).length}
                  </span>
                </div>
                <input
                  className="breakdown-search"
                  placeholder="Search..."
                  value={zeroSearch}
                  onChange={e => setZeroSearch(e.target.value)}
                />
              </div>
              <div className="breakdown-col-labels">
                <span>Ad Name</span>
                <span>Account</span>
              </div>
              {breakdown.loading ? (
                <BreakdownSkeleton />
              ) : breakdown.zero.filter(a =>
                  !zeroSearch ||
                  a.builder_name?.toLowerCase().includes(zeroSearch.toLowerCase()) ||
                  a.account_user?.toLowerCase().includes(zeroSearch.toLowerCase())
                ).length === 0 ? (
                <div className="breakdown-empty">
                  {zeroSearch ? 'No results found' : 'All ads have leads'}
                </div>
              ) : (
                <div className="breakdown-scroll">
                  {breakdown.zero
                    .filter(a =>
                      !zeroSearch ||
                      a.builder_name?.toLowerCase().includes(zeroSearch.toLowerCase()) ||
                      a.account_user?.toLowerCase().includes(zeroSearch.toLowerCase())
                    )
                    .map(ad => <AdRow key={ad.id} ad={ad} showCount={false} />)
                  }
                </div>
              )}
            </div>

            {/* Greater Than Zero Leads */}
            <div className="breakdown-panel breakdown-nonzero">
              <div className="breakdown-header">
                <div className="breakdown-header-left">
                  <span className="breakdown-title">Greater Than Zero Leads <span className="breakdown-date-tag">Yesterday</span></span>
                  <span className="breakdown-badge badge-nonzero">
                    {breakdown.loading ? '—' : breakdown.nonZero.filter(a =>
                      !nonZeroSearch ||
                      a.builder_name?.toLowerCase().includes(nonZeroSearch.toLowerCase()) ||
                      a.account_user?.toLowerCase().includes(nonZeroSearch.toLowerCase())
                    ).length}
                  </span>
                </div>
                <input
                  className="breakdown-search"
                  placeholder="Search..."
                  value={nonZeroSearch}
                  onChange={e => setNonZeroSearch(e.target.value)}
                />
              </div>
              <div className="breakdown-col-labels">
                <span>Ad Name</span>
                <span>Account</span>
                <span>Leads</span>
              </div>
              {breakdown.loading ? (
                <BreakdownSkeleton />
              ) : breakdown.nonZero.filter(a =>
                  !nonZeroSearch ||
                  a.builder_name?.toLowerCase().includes(nonZeroSearch.toLowerCase()) ||
                  a.account_user?.toLowerCase().includes(nonZeroSearch.toLowerCase())
                ).length === 0 ? (
                <div className="breakdown-empty">
                  {nonZeroSearch ? 'No results found' : 'No ads with leads yet'}
                </div>
              ) : (
                <div className="breakdown-scroll">
                  {breakdown.nonZero
                    .filter(a =>
                      !nonZeroSearch ||
                      a.builder_name?.toLowerCase().includes(nonZeroSearch.toLowerCase()) ||
                      a.account_user?.toLowerCase().includes(nonZeroSearch.toLowerCase())
                    )
                    .map(ad => <AdRow key={ad.id} ad={ad} showCount={true} />)
                  }
                </div>
              )}
            </div>

          </div>

        </main>
      </div>
    </div>
  );
};

export default Home;
